// src/modules/smelter/pages/DispatchDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDispatchById, confirmReceipt, overrideTotalAmount, recordPayment, deleteDispatch,
  reverseLastPaymentReceipt,
  getSmelterInvoice, generateSmelterInvoice, downloadSmelterInvoicePdf, fetchDeliveryEmployees,
  fetchNextInvoiceNo, fetchGoldGstRate,
} from '../api';
import {
  Card, Typography, Spin, Tag, Button, Row, Col, Descriptions, Table,
  message, Space, Steps, Modal, Form, InputNumber, Input, Select, Statistic, Tooltip, Divider, DatePicker, Grid, Alert, Checkbox, Popconfirm, Popover, Progress,
} from 'antd';
import { fetchFunds, getAccounts } from '../../accounting/api';
import { hsnForMetal, goodsDescriptionForMetal } from '../constants';
import { ArrowLeftOutlined, CheckCircleOutlined, DollarOutlined, ArrowUpOutlined, ArrowDownOutlined, PlusOutlined, DeleteOutlined, FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import { rupeesInWords } from '../../../utils/amountInWords';
import TransitMemoModal from '../components/TransitMemoModal';

const { Title, Text } = Typography;

const STATUSES = ['DISPATCHED', 'RECEIPT_CONFIRMED', 'PAYMENT_RECEIVED'];
const STATUS_COLORS = { DISPATCHED: 'orange', RECEIPT_CONFIRMED: 'blue', PAYMENT_RECEIVED: 'green' };
const PAYMENT_MODES = [
  { value: 'NEFT', label: 'NEFT' },
  { value: 'RTGS', label: 'RTGS' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
];

// Built-in Sundry Debtors account — preselected in the Confirm Receipt picker.
const DEFAULT_DEBTOR_CODE = '1305';
// Built-in TDS Receivable account — preselected in the Record Payment picker.
const DEFAULT_TDS_CODE = '1340';
// Mirrors the backend's payment-mode routing, used only to prefill the per-split
// account picker; the user can override it and the backend honours what is sent.
const MODE_DEFAULT_CODE = (mode) => (String(mode || '').toUpperCase().replace(/\s+/g, '_') === 'CASH' ? '1000' : '1100');
const ACCOUNT_TYPE_ORDER = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
const fmt2 = (v) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const round2Wt = (v) => Math.round((Number(v) || 0) * 10000) / 10000;

// Where a dispatch stands against its bill — mirrors smelterPaymentService's
// settlementState(). `outstanding` is what may still be received; `toSettle` is
// what is still needed to CLOSE the dispatch (the metal value), which is smaller
// whenever the smelter has yet to reimburse GST.
// A dispatch paid by the old one-shot flow has no receipts — read its payment out
// of the legacy fields so it is never shown as unpaid. Mirrors the backend's
// legacyReceipts(); the row is materialised server-side the first time a further
// receipt is taken.
const legacyReceiptsOf = (dispatch) => {
  if (!dispatch) return [];
  const cash = round2((dispatch.paymentSplits || []).reduce((s, p) => s + (Number(p.amount) || 0), 0));
  const tds = round2(dispatch.tdsAmount || 0);
  if (cash <= 0 && tds <= 0) {
    if (dispatch.status !== 'PAYMENT_RECEIVED') return [];
    const legacy = round2(dispatch.profitBreakdown?.totalAmountFromSmelter || dispatch.totalAmountFromSmelter || 0);
    return legacy > 0
      ? [{ seq: 1, legacy: true, source: 'RECORD_PAYMENT', splits: [], cashAmount: legacy, tdsAmount: 0, settledAmount: legacy, gstRecovered: 0, date: dispatch.paymentDate }]
      : [];
  }
  return [{
    seq: 1, legacy: true, source: 'RECORD_PAYMENT',
    splits: dispatch.paymentSplits || [],
    cashAmount: cash, tdsAmount: tds, tdsAccountId: dispatch.tdsAccountId || null,
    settledAmount: round2(cash + tds), gstRecovered: 0, date: dispatch.paymentDate,
  }];
};

const settlementOf = (dispatch, invoice) => {
  const invoiceTotal = round2(invoice?.total ?? dispatch?.totalAmountFromSmelter ?? 0);
  const metalValue = round2(invoice?.taxableValue ?? dispatch?.taxableAmountFromSmelter ?? invoiceTotal);
  const receipts = dispatch?.paymentReceipts?.length ? dispatch.paymentReceipts : legacyReceiptsOf(dispatch);
  const cash = round2(receipts.reduce((s, r) => s + (Number(r.cashAmount) || 0), 0));
  const tds = round2(receipts.reduce((s, r) => s + (Number(r.tdsAmount) || 0), 0));
  const settled = round2(cash + tds);
  return {
    invoiceTotal,
    metalValue,
    cashReceived: cash,
    tdsReceived: tds,
    settled,
    outstanding: round2(Math.max(0, invoiceTotal - settled)),
    toSettle: round2(Math.max(0, metalValue - settled)),
    gstRecovered: round2(Math.max(0, settled - metalValue)),
    receipts,
    isSettled: dispatch?.status === 'PAYMENT_RECEIVED',
    isPartlyPaid: settled > 0 && dispatch?.status !== 'PAYMENT_RECEIVED',
  };
};

// Mirror of the backend's allocate() — splits a whole pro-rata across rows with the
// residue on the last contributing row, so the parts always add back to the whole.
const allocate = (whole, basis, decimals) => {
  const f = Math.pow(10, decimals);
  const rnd = (v) => Math.round(v * f) / f;
  const out = basis.map(() => 0);
  const sum = basis.reduce((acc, b) => acc + (Number(b) || 0), 0);
  let last = -1;
  basis.forEach((b, i) => { if ((Number(b) || 0) > 0) last = i; });
  if (last === -1) {
    if (out.length) out[out.length - 1] = rnd(whole);
    return out;
  }
  let running = 0;
  basis.forEach((b, i) => {
    if (i === last) return;
    out[i] = rnd((whole * (Number(b) || 0)) / sum);
    running += out[i];
  });
  out[last] = rnd(whole - running);
  return out;
};

// Mirror of the backend's utils/gstUtils.splitGst. CGST and SGST are the same
// percentage, so a bill has to print the SAME figure twice — never 331.83 against
// 331.82. Each head is charged on the taxable value and both take that one figure.
const splitGst = (taxableValue, gstRatePercent, isIntra = true) => {
  const taxable = round2(taxableValue);
  const rate = Number(gstRatePercent) || 0;
  if (!isIntra) {
    const igst = round2((taxable * rate) / 100);
    return { cgst: 0, sgst: 0, igst, gst: igst };
  }
  const half = round2((taxable * (rate / 2)) / 100);
  return { cgst: half, sgst: half, igst: 0, gst: round2(half * 2) };
};

// Mirror of the backend's utils/gstUtils.computeTaxInclusive. The smelter pays one
// GST-INCLUSIVE total; the GST inside it is split first (equal heads) and the taxable
// value takes the remainder, so taxable + GST is exactly the inclusive total and the
// half-paisa a 1.5% split can throw off is carried by the taxable, not by one head.
const backOutGst = (totalInclGst, gstRatePercent, isIntra = true) => {
  const total = round2(totalInclGst);
  const base = round2(total / (1 + (Number(gstRatePercent) || 0) / 100));
  const split = splitGst(base, gstRatePercent, isIntra);
  return { total, taxable: round2(total - split.gst), ...split };
};

export default function DispatchDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEditModule, canViewField, can } = usePermissions();
  // Margin figures are gated separately from the dispatch record itself — see
  // the `inventoryProfit` module in the permission catalog.
  const canViewProfit = can('inventoryProfit', 'view');
  const screens = Grid.useBreakpoint();

  const canViewSmelterName = canViewField('smelter', 'name');
  const canViewContact = canViewField('smelter', 'contactPerson');
  const canViewPhone = canViewField('smelter', 'phone');
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiptModal, setReceiptModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [overrideModal, setOverrideModal] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [memoModal, setMemoModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [funds, setFunds] = useState([]);
  const [fundsLoaded, setFundsLoaded] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [gstRate, setGstRate] = useState(3);
  // The smelter's own weighed gross usually equals what we dispatched; the override
  // is only for the rare case where their scale disagrees with ours.
  const [grossOverride, setGrossOverride] = useState(false);
  const [receiptForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [overrideForm] = Form.useForm();
  const [invoiceForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const loadInvoice = () => {
    getSmelterInvoice(id)
      .then((r) => setInvoice(r.data))
      .catch(() => setInvoice(null)); // 404 = not generated yet
  };

  const load = () => {
    setLoading(true);
    getDispatchById(id)
      .then((r) => {
        setDispatch(r.data);
        if (['RECEIPT_CONFIRMED', 'PAYMENT_RECEIVED'].includes(r.data?.status)) loadInvoice();
      })
      .catch(() => messageApi.error('Failed to load dispatch'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  // Load employee list for the "metal delivered by" dropdown when the modal opens.
  useEffect(() => {
    if (invoiceModal && employees.length === 0) {
      fetchDeliveryEmployees()
        .then((r) => setEmployees(r.data || []))
        .catch(() => messageApi.error('Failed to load employee list'));
    }
  }, [invoiceModal]);

  // Confirm Receipt asks for exactly two numbers off the smelter's bill — the pure
  // (refined) weight and the GST-inclusive total. Everything else is derived.
  useEffect(() => {
    if (!receiptModal || !dispatch) return;
    const smelterGross = dispatch.receiptGrossWeight ?? dispatch.dispatchedGrossWeight;
    setGrossOverride(
      dispatch.receiptGrossWeight != null
      && round2Wt(dispatch.receiptGrossWeight) !== round2Wt(dispatch.dispatchedGrossWeight)
    );
    receiptForm.setFieldsValue({
      refinedWeight: dispatch.refinedWeight ?? null,
      totalAmountFromSmelter: dispatch.totalAmountFromSmelter || null,
      receiptGrossWeight: smelterGross,
    });
  }, [receiptModal, dispatch, receiptForm]);

  // The GST rate in force — the receipt preview must back GST out of the same rate
  // the invoice will use, so it is read from the invoice profile, not hard-coded.
  useEffect(() => {
    if (!receiptModal && !invoiceModal) return;
    fetchGoldGstRate()
      .then((r) => { if (r?.data?.gstRatePercent > 0) setGstRate(Number(r.data.gstRatePercent)); })
      .catch(() => {/* keep the 3% default */});
  }, [receiptModal, invoiceModal]);

  // Chart of Accounts for the "Sundry Debtors" picker. The account chosen on
  // Confirm Receipt is what the bill debits and what the payment later clears, so
  // it is fixed once, before the invoice is raised.
  useEffect(() => {
    if ((!receiptModal && !paymentModal) || accountsLoaded) return;
    getAccounts({ isActive: true })
      .then((r) => { setAccounts(r.data || []); setAccountsLoaded(true); })
      .catch(() => { setAccountsLoaded(true); messageApi.error('Failed to load the chart of accounts'); });
  }, [receiptModal, paymentModal, accountsLoaded]);

  useEffect(() => {
    if (!receiptModal || !accounts.length) return;
    const current = dispatch?.debtorAccountId?._id || dispatch?.debtorAccountId;
    const fallback = accounts.find((a) => a.code === DEFAULT_DEBTOR_CODE);
    receiptForm.setFieldsValue({ debtorAccountId: current || fallback?._id });
  }, [receiptModal, accounts, dispatch, receiptForm]);

  useEffect(() => {
    if (paymentModal && invoice?.taxableValue != null) {
      // Default to whatever is still needed to close the dispatch — for a first
      // full payment that is the metal value, for a follow-up instalment it is the
      // remainder. Freely editable: the smelter may be paying only part of it.
      const st = settlementOf(dispatch, invoice);
      paymentForm.setFieldsValue({
        paymentSplits: [{ mode: 'BANK_TRANSFER', amount: st.isSettled ? st.outstanding : (st.toSettle || st.outstanding), reference: '' }],
        tdsAmount: null,
      });
    }
  }, [paymentModal, invoice, dispatch, paymentForm]);

  // Preselect the ledger accounts on Record Payment once the chart of accounts is
  // in: the bank/cash account each split is debited to, and the TDS account the
  // smelter's deduction is debited to. Both stay editable.
  useEffect(() => {
    if (!paymentModal || !accounts.length) return;
    const byCode = (code) => accounts.find((a) => a.code === code)?._id;
    const splits = paymentForm.getFieldValue('paymentSplits') || [];
    paymentForm.setFieldsValue({
      tdsAccountId: paymentForm.getFieldValue('tdsAccountId') || byCode(DEFAULT_TDS_CODE),
      paymentSplits: splits.map((sp) => ({
        ...sp,
        accountId: sp?.accountId || byCode(MODE_DEFAULT_CODE(sp?.mode)),
      })),
    });
  }, [paymentModal, accounts, paymentForm]);

  // Load Manage Company Funds when the payment modal opens; preselect the
  // fund flagged as the Smelter Payment Fund.
  useEffect(() => {
    if (!paymentModal) return;
    fetchFunds()
      .then((r) => {
        const list = (r.data || []).filter((f) => f.isActive !== false);
        setFunds(list);
        setFundsLoaded(true);
        const flagged = list.find((f) => f.isSmelterPaymentFund);
        if (flagged) paymentForm.setFieldsValue({ fundId: flagged._id });
      })
      .catch(() => {
        setFundsLoaded(true);
        messageApi.error('Failed to load company funds');
      });
  }, [paymentModal, paymentForm]);

  const smelterFlaggedFund = funds.find((f) => f.isSmelterPaymentFund);

  // Prefill the editable invoice modal — from an existing invoice, else derive
  // sensible defaults from the dispatch + smelter. GST is treated as INCLUSIVE:
  // the smelter's total is the anchor and the taxable value + GST are derived from
  // it (taxable = total / (1 + rate); GST = total − taxable).
  useEffect(() => {
    if (!invoiceModal) return;
    const sm = dispatch?.smelterId || {};
    if (invoice) {
      invoiceForm.setFieldsValue({
        invoiceNo: invoice.invoiceNo || '',
        invoiceDate: invoice.invoiceDate ? dayjs(invoice.invoiceDate) : dayjs(),
        deliveredByUserId: invoice.deliveredBy?.userId || undefined,
        placeOfSupply: invoice.placeOfSupply || '',
        reverseCharge: !!invoice.reverseCharge,
        gstType: invoice.gstType || 'INTRA',
        buyer: {
          name: invoice.buyer?.name || '', gstin: invoice.buyer?.gstin || '',
          pan: invoice.buyer?.pan || '', state: invoice.buyer?.state || '',
          address: invoice.buyer?.address || '',
        },
        lineItems: (invoice.lineItems || []).map((li) => ({ ...li })),
        taxableValue: invoice.taxableValue,
        cgstPercent: invoice.cgstPercent, cgstAmount: invoice.cgstAmount,
        sgstPercent: invoice.sgstPercent, sgstAmount: invoice.sgstAmount,
        igstPercent: invoice.igstPercent, igstAmount: invoice.igstAmount,
        roundOff: invoice.roundOff,
        // The `total` field is the pre-round-off anchor the taxable is backed out of,
        // so rebuild it from the taxable value before the round-off was applied.
        total: round2(
          (round2(invoice.taxableValue) - round2(invoice.roundOff))
          * (1 + ((invoice.gstType === 'INTER'
            ? (invoice.igstPercent || 0)
            : (invoice.cgstPercent || 0) + (invoice.sgstPercent || 0)) / 100))
        ),
        notes: invoice.notes || '',
      });
      return;
    }
    // Defaults from dispatch — the dispatch amount is GST-INCLUSIVE; taxable and GST
    // are backed out of it (taxable = total / (1 + rate)), matching the fly form.
    const netWeight = round2Wt(dispatch?.refinedWeight);
    // Gross = smelter's weighed gross (GROUPED) if present, else what we dispatched.
    const grossWeight = round2Wt(dispatch?.receiptGrossWeight ?? dispatch?.dispatchedGrossWeight ?? netWeight);
    const meltingLoss = round2Wt(Math.max(0, grossWeight - netWeight));
    const total = round2(dispatch?.totalAmountFromSmelter); // GST-inclusive amount
    const rate = gstRate; // e.g. 3 → CGST 1.5 + SGST 1.5
    const { taxable, gst, cgst: cgstAmount, sgst: sgstAmount } = backOutGst(total, rate);
    // Line amount = pre-GST (taxable); effective pre-GST rate = taxable / net.
    const lineRate = netWeight > 0 ? round2(taxable / netWeight) : round2(dispatch?.rateOfferedBySmelter);
    invoiceForm.setFieldsValue({
      invoiceNo: '',
      invoiceDate: dayjs(),
      deliveredByUserId: undefined,
      placeOfSupply: sm.state || '',
      reverseCharge: false,
      gstType: 'INTRA',
      buyer: { name: sm.name || '', gstin: sm.gstin || '', pan: sm.pan || '', state: sm.state || '', address: sm.address || '' },
      lineItems: [{
        description: goodsDescriptionForMetal(dispatch?.metalType),
        hsn: hsnForMetal(dispatch?.metalType),
        grossWeight, meltingLoss, netWeight, rate: lineRate, amount: taxable,
      }],
      taxableValue: taxable,
      cgstPercent: rate / 2, cgstAmount,
      sgstPercent: rate / 2, sgstAmount,
      igstPercent: rate, igstAmount: gst,
      roundOff: 0,
      total,
      notes: '',
    });
    // Pre-fill the editable invoice number with the next AGS series number.
    fetchNextInvoiceNo()
      .then((res) => {
        if (res.success && res.data?.invoiceNo) invoiceForm.setFieldsValue({ invoiceNo: res.data.invoiceNo });
      })
      .catch(() => {/* leave blank */});
  }, [invoiceModal, invoice, dispatch, invoiceForm, gstRate]);

  const handleConfirmReceipt = async (values) => {
    setActionLoading('receipt');
    try {
      await confirmReceipt(id, {
        refinedWeight: values.refinedWeight,
        totalAmountFromSmelter: values.totalAmountFromSmelter,
        // Only sent when the smelter's scale disagreed with our dispatched gross;
        // otherwise the backend uses the dispatched gross itself.
        ...(grossOverride && values.receiptGrossWeight != null
          ? { receiptGrossWeight: values.receiptGrossWeight } : {}),
        debtorAccountId: values.debtorAccountId,
        receiptNotes: values.receiptNotes,
      });
      messageApi.success('Receipt confirmed');
      setReceiptModal(false);
      receiptForm.resetFields();
      load();
    } catch (e) { messageApi.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const doRecordPayment = async (values) => {
    setActionLoading('payment');
    try {
      const res = await recordPayment(id, {
        paymentSplits: values.paymentSplits,
        paymentDate: values.paymentDate?.toISOString(),
        fundId: values.fundId,
        tdsAmount: Number(values.tdsAmount) || 0,
        tdsAccountId: Number(values.tdsAmount) > 0 ? values.tdsAccountId : null,
      });
      messageApi.success(res?.message || 'Payment recorded');
      setPaymentModal(false);
      paymentForm.resetFields();
      load();
    } catch (e) { messageApi.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const handleReverseReceipt = async () => {
    setActionLoading('reverse-receipt');
    try {
      const res = await reverseLastPaymentReceipt(id);
      messageApi.success(res?.message || 'Receipt reversed');
      load();
    } catch (e) { messageApi.error(e.response?.data?.message || 'Failed to reverse the receipt'); }
    finally { setActionLoading(''); }
  };

  // Submit → confirm the destination fund first, then record.
  const handleRecordPayment = (values) => {
    const fund = funds.find((f) => f._id === values.fundId);
    const cashNow = round2((values.paymentSplits || []).reduce((s, p) => s + (Number(p?.amount) || 0), 0));
    if (!fund && cashNow > 0) {
      messageApi.error('Select the fund this payment goes into');
      return;
    }
    // A TDS-only instalment moves no cash, so there is no fund credit to confirm.
    if (cashNow <= 0) { doRecordPayment(values); return; }
    // Only the cash actually received is credited to the fund — TDS never arrives.
    const creditAmount = round2((values.paymentSplits || []).reduce((s, p) => s + (Number(p?.amount) || 0), 0));
    const tds = round2(values.tdsAmount);
    const balanceBefore = Number(fund.balance) || 0;
    const balanceAfter = round2(balanceBefore + creditAmount);

    Modal.confirm({
      title: 'Confirm Fund Credit',
      width: 460,
      content: (
        <div style={{ fontSize: 13 }}>
          <p style={{ marginBottom: 8 }}>This payment will be credited into:</p>
          <div style={{ padding: '10px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>Fund</span>
              <strong>
                {fund.name}{' '}
                {fund.isSmelterPaymentFund && <Tag color="green" style={{ marginLeft: 4 }}>Smelter Fund</Tag>}
              </strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>Amount to credit</span><strong style={{ color: '#3f8600' }}>{fmt(creditAmount)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>Balance now</span><span>{fmt(balanceBefore)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Balance after</span><strong>{fmt(balanceAfter)}</strong>
            </div>
          </div>
          {tds > 0 && (
            <p style={{ marginTop: 8, marginBottom: 0, color: '#595959' }}>
              TDS of <strong>{fmt(tds)}</strong> deducted by the smelter is <em>not</em> credited to the fund —
              it is debited to the TDS account and clears the debtor along with the cash
              (total settled {fmt(round2(creditAmount + tds))}).
            </p>
          )}
          <p style={{ marginTop: 8, marginBottom: 0, color: '#595959' }}>
            {settlement.isSettled
              ? <>{dispatch.dispatchId} is already settled — this clears GST off its debtor balance.</>
              : round2(creditAmount + tds) >= settlement.toSettle - 0.01
                ? <>This <strong>settles</strong> {dispatch.dispatchId} — profit is calculated and the linked cases close.</>
                : <>This is a <strong>part payment</strong>. {fmt(round2(settlement.toSettle - creditAmount - tds))} will
                  still be due before {dispatch.dispatchId} settles.</>}
          </p>
          {!fund.isSmelterPaymentFund && (
            <p style={{ marginTop: 8, color: '#fa8c16' }}>
              Note: this is not the flagged Smelter Payment Fund ({smelterFlaggedFund?.name || '—'}).
            </p>
          )}
        </div>
      ),
      okText: 'Yes, Record Payment',
      cancelText: 'Back',
      onOk: () => doRecordPayment(values),
    });
  };

  // ── Recompute helpers for the editable invoice modal ──
  // "Total from Smelter (incl. GST)" is the anchor the taxable value is backed out
  // of. Round Off then adjusts THAT taxable value, and GST is charged on the
  // adjusted figure — so a round-off moves the GST and the grand total with it:
  //   taxable = (anchor − GST inside it) + roundOff
  //   gst     = equal heads charged on taxable      grand = taxable + gst
  // With no round-off the bill ties back to the anchor exactly.
  const recomputeTotals = () => {
    const v = invoiceForm.getFieldsValue();
    const anchor = Number(v.total) || 0;
    const isIntra = v.gstType !== 'INTER';
    const rate = isIntra
      ? (Number(v.cgstPercent) || 0) + (Number(v.sgstPercent) || 0)
      : (Number(v.igstPercent) || 0);
    // With no adjustment the taxable is backed out of the anchor, so the bill ties to
    // the paisa with what the smelter paid. Once Round Off moves the taxable, GST is
    // charged forward on the adjusted figure. Either way CGST and SGST are identical.
    const roundOff = Number(v.roundOff) || 0;
    const incl = backOutGst(anchor, rate, isIntra);
    const taxable = round2(incl.taxable + roundOff);
    const split = roundOff === 0 ? incl : splitGst(taxable, rate, isIntra);
    const cgstAmount = split.cgst, sgstAmount = split.sgst, igstAmount = split.igst;

    const items = invoiceForm.getFieldValue('lineItems') || [];
    if (items[0]) {
      const net = Number(items[0].netWeight) || 0;
      items[0] = { ...items[0], amount: taxable, rate: net > 0 ? round2(taxable / net) : items[0].rate };
    }
    invoiceForm.setFieldsValue({
      lineItems: items,
      taxableValue: taxable,
      cgstAmount: isIntra ? cgstAmount : 0,
      sgstAmount: isIntra ? sgstAmount : 0,
      igstAmount: !isIntra ? igstAmount : 0,
    });
  };

  // The billed total: the round-off already sits inside the taxable value, so the
  // grand total is simply taxable + GST. Reads the fields recomputeTotals keeps in
  // sync, so the preview, the TOTAL row and the payload can never disagree.
  const grandTotalOf = (get) => {
    const taxable = round2(get('taxableValue'));
    const gst = get('gstType') === 'INTER'
      ? round2(get('igstAmount'))
      : round2((Number(get('cgstAmount')) || 0) + (Number(get('sgstAmount')) || 0));
    return round2(taxable + gst);
  };
  // Invariant across the three weights: Gross = Net + Melting Loss.
  // Editing Gross → keep Net, adjust Melting Loss (= Gross − Net).
  const onLineGrossChange = (idx) => {
    const items = invoiceForm.getFieldValue('lineItems') || [];
    const it = items[idx] || {};
    const gross = Number(it.grossWeight) || 0;
    const net = Number(it.netWeight) || 0;
    items[idx] = { ...it, meltingLoss: round2Wt(Math.max(0, gross - net)) };
    invoiceForm.setFieldsValue({ lineItems: items });
  };
  // Editing Melting Loss → keep Gross, derive Net (= Gross − Loss); refresh pre-GST rate.
  const onLineMeltLossChange = (idx) => {
    const items = invoiceForm.getFieldValue('lineItems') || [];
    const it = items[idx] || {};
    const gross = Number(it.grossWeight) || 0;
    const loss = Number(it.meltingLoss) || 0;
    const net = round2Wt(Math.max(0, gross - loss));
    items[idx] = { ...it, netWeight: net };
    invoiceForm.setFieldsValue({ lineItems: items });
    recomputeTotals();
  };
  // Editing Net → adjust Melting Loss (= Gross − Net); refresh pre-GST rate.
  const onLineNetChange = (idx) => {
    const items = invoiceForm.getFieldValue('lineItems') || [];
    const it = items[idx] || {};
    const gross = Number(it.grossWeight) || 0;
    const net = Number(it.netWeight) || 0;
    items[idx] = { ...it, meltingLoss: round2Wt(Math.max(0, gross - net)) };
    invoiceForm.setFieldsValue({ lineItems: items });
    recomputeTotals();
  };

  const handleGenerateInvoice = async (values) => {
    setActionLoading('invoice');
    try {
      // Grand total = billed taxable + GST; the round-off is already inside the taxable.
      const total = grandTotalOf((k) => values[k]);
      // Melting Loss on each line = Gross − Net.
      const lineItems = (values.lineItems || []).map((li) => ({
        ...li,
        meltingLoss: round2Wt(Math.max(0, (Number(li.grossWeight) || 0) - (Number(li.netWeight) || 0))),
      }));
      const payload = {
        ...values,
        lineItems,
        invoiceDate: values.invoiceDate?.toISOString(),
        total,
      };
      const r = await generateSmelterInvoice(id, payload);
      setInvoice(r.data);
      setInvoiceModal(false);
      messageApi.success(`Invoice ${r.data.invoiceNo} generated`);
      // Ask which copy to download (ORIGINAL BILL / DUPLICATE BILL on top).
      setGeneratedCopyPrompt(r.data.invoiceNo);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to generate invoice');
    } finally { setActionLoading(''); }
  };

  const [invoiceCopyOpen, setInvoiceCopyOpen] = useState(false);
  // Invoice no. of a just-generated bill awaiting the Original / Duplicate choice.
  const [generatedCopyPrompt, setGeneratedCopyPrompt] = useState(null);

  const handleDownloadInvoice = async (copy) => {
    if (!invoice) return;
    setInvoiceCopyOpen(false);
    setGeneratedCopyPrompt(null);
    setActionLoading('download-invoice');
    try {
      await downloadSmelterInvoicePdf(id, invoice.invoiceNo, copy);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to download invoice');
    } finally { setActionLoading(''); }
  };

  const handleOverrideAmount = async (values) => {
    setActionLoading('override');
    try {
      await overrideTotalAmount(id, values);
      messageApi.success('Total amount overridden successfully');
      setOverrideModal(false);
      overrideForm.resetFields();
      load();
    } catch (e) { messageApi.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteDispatch(id);
      messageApi.success('Dispatch deleted and its journal entries reversed');
      navigate('/smelters/dispatches');
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to delete dispatch');
    } finally {
      setDeleting(false);
    }
  };

  // Deleting unwinds the ledger too, so spell out what gets reversed first.
  const handleDelete = () => {
    Modal.confirm({
      title: `Delete dispatch ${dispatch?.dispatchId}?`,
      width: 520,
      okText: 'Delete & Reverse',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      content: (
        <div style={{ fontSize: 13 }}>
          <p style={{ marginTop: 0 }}>The metal returns to inventory and the linked cases are reopened. In the ledger:</p>
          <ul style={{ paddingLeft: 18, margin: '0 0 8px' }}>
            <li>The dispatch entry (Metal In Transit ← Inventory) is <b>reversed</b>.</li>
            {invoice && (
              <li>
                The sale entry for bill <b>{invoice.invoiceNo}</b> (Sundry Debtors ← In Transit / GST / Revenue)
                is <b>reversed</b>, and the bill itself is deleted.
              </li>
            )}
          </ul>
          <p style={{ margin: 0, color: '#8c8c8c' }}>
            Both originals stay on record as REVERSED with their counter-entries, so the audit trail is preserved.
          </p>
        </div>
      ),
      onOk: doDelete,
    });
  };

  const caseColumns = [
    { title: 'Type', key: 'type', width: 80,
      render: (_, r) => (
        <Tag color={r.sourceType === 'PLEDGE' ? 'purple' : 'blue'}>
          {r.sourceType === 'PLEDGE' ? 'Pledge' : 'Walk-In'}
        </Tag>
      ) },
    { title: 'Case', key: 'case', width: 130,
      render: (_, r) => {
        const caseLink = r.sourceType === 'PLEDGE'
          ? `/pledge-release-cases/${r.caseId?._id || r.caseId}`
          : `/walk-in-cases/${r.caseId?._id || r.caseId}`;
        return (
          <Button type="link" size="small" onClick={() => navigate(caseLink)}>
            {r.caseNumber || r.caseId?.caseId || '—'}
          </Button>
        );
      } },
    { title: 'Customer', key: 'customer', width: 140,
      render: (_, r) => r.caseId?.customerId?.name || '—' },
    { title: 'Net Wt (g)', dataIndex: 'netWeight', key: 'nw', render: (v) => v?.toFixed(4) },
    { title: 'Purchase Cost', dataIndex: 'purchaseCost', key: 'pc', render: (v) => fmt(v) },
  ];

  // Everything the payment UI keys off: what has been received, what is still owed
  // and what is still needed to close the dispatch.
  const settlement = settlementOf(dispatch, invoice);
  const lastReceipt = settlement.receipts[settlement.receipts.length - 1] || null;
  // Undoable while it did not close the dispatch: either the dispatch is still
  // open, or this receipt came in after settlement (GST being reimbursed), which
  // carries no profit allocation with it.
  const canUndoReceipt = !!lastReceipt
    && !lastReceipt.legacy
    && lastReceipt.source !== 'MANUAL_JOURNAL'
    && (!settlement.isSettled
      || round2(settlement.settled - lastReceipt.settledAmount) >= settlement.metalValue - 0.01)
    && canEditModule('smelter');

  if (loading) return <Spin style={{ display: 'block', margin: 80, textAlign: 'center' }} />;
  if (!dispatch) return null;

  const stepIdx = STATUSES.indexOf(dispatch.status);
  const batch = dispatch.inventoryBatchId || {};
  const profit = dispatch.profitBreakdown || {};
  const isItemized = dispatch.batchType === 'ITEMIZED';

  // Diff indicator helper
  const diffCell = (val, decimals = 3) => {
    if (val == null || val === 0) return <Text type="secondary">—</Text>;
    const color = val > 0 ? '#3f8600' : '#cf1322';
    const Icon = val > 0 ? ArrowUpOutlined : ArrowDownOutlined;
    return <Text style={{ color }}><Icon style={{ fontSize: 10 }} /> {Math.abs(val).toFixed(decimals)}</Text>;
  };

  const dispatchItemColumns = [
    { title: 'Label', dataIndex: 'label', key: 'label', width: 130, fixed: 'left' },
    {
      title: <Tooltip title="Dispatched by us"><span style={{ color: '#1677ff' }}>Our Figures</span></Tooltip>,
      children: [
        { title: 'Gross (g)', dataIndex: 'grossWeight', key: 'gw', width: 90, render: (v) => v?.toFixed(3) },
        { title: 'Purity %', dataIndex: 'purityPercent', key: 'pu', width: 80, render: (v) => v ? `${v.toFixed(2)}%` : '—' },
        { title: 'Net (g)', dataIndex: 'netWeight', key: 'nw', width: 85, render: (v) => v?.toFixed(4) },
        { title: 'Price (₹)', dataIndex: 'priceAmount', key: 'pa', width: 100, render: (v) => fmt(v) },
      ],
    },
    {
      title: <Tooltip title="As confirmed by smelter"><span style={{ color: '#52c41a' }}>Smelter Confirmed</span></Tooltip>,
      children: [
        { title: 'Melting Loss (g)', dataIndex: 'meltingLoss', key: 'ml', width: 90, render: (v) => v != null ? v.toFixed(3) : '—' },
        { title: 'Gross (g)', dataIndex: 'receiptGrossWeight', key: 'rg', width: 90, render: (v) => v != null ? v.toFixed(3) : '—' },
        { title: 'Purity %', dataIndex: 'receiptPurityPercent', key: 'rp', width: 80, render: (v) => v != null ? `${v.toFixed(2)}%` : '—' },
        { title: 'Net (g)', dataIndex: 'receiptNetWeight', key: 'rn', width: 85, render: (v) => v != null ? v.toFixed(4) : '—' },
        { title: 'Rate (₹/g, ex-GST)', dataIndex: 'receiptRatePerGram', key: 'rr', width: 110, render: (v) => v != null ? fmt(v) : '—' },
        { title: 'Taxable (₹)', dataIndex: 'receiptAmount', key: 'ra', width: 105, render: (v) => v != null ? <Text strong>{fmt(v)}</Text> : '—' },
      ],
    },
    {
      title: <Tooltip title="Smelter value minus our value"><span style={{ color: '#fa8c16' }}>Δ Difference</span></Tooltip>,
      children: [
        { title: 'ΔGross (g)', dataIndex: 'grossWeightDiff', key: 'dg', width: 90, render: (v) => diffCell(v, 3) },
        { title: 'ΔPurity', dataIndex: 'purityDiff', key: 'dp', width: 80, render: (v) => diffCell(v, 2) },
        { title: 'ΔNet (g)', dataIndex: 'netWeightDiff', key: 'dn', width: 85, render: (v) => diffCell(v, 4) },
      ],
    },
  ];

  const thStyle = { padding: '6px 8px', borderBottom: '2px solid #f0f0f0', textAlign: 'center', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' };
  const tdStyle = { padding: '6px 8px', verticalAlign: 'middle', textAlign: 'center', fontSize: 12 };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      {contextHolder}

      <Modal
        open={!!generatedCopyPrompt}
        title={`Invoice ${generatedCopyPrompt || ''} generated`}
        onCancel={() => setGeneratedCopyPrompt(null)}
        footer={[
          <Button key="skip" onClick={() => setGeneratedCopyPrompt(null)}>
            Skip
          </Button>,
          <Button key="duplicate" onClick={() => handleDownloadInvoice('duplicate')}>
            Duplicate
          </Button>,
          <Button key="original" type="primary" onClick={() => handleDownloadInvoice('original')}>
            Original
          </Button>,
        ]}
      >
        Which copy do you want to download?
      </Modal>

      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/smelters/dispatches')}>Back</Button>
            <Title level={4} style={{ margin: 0 }}>
              {dispatch.dispatchId} &nbsp;
              <Tag color={STATUS_COLORS[dispatch.status]}>{dispatch.status?.replace(/_/g, ' ')}</Tag>
              {settlement.isPartlyPaid && (
                <Tag color="gold" style={{ marginLeft: 4 }}>
                  PARTLY PAID — {fmt(settlement.settled)} of {fmt(settlement.invoiceTotal)}
                </Tag>
              )}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            {dispatch.status === 'DISPATCHED' && canEditModule('smelter') && (
              <Button icon={<CheckCircleOutlined />} onClick={() => setReceiptModal(true)}
                loading={actionLoading === 'receipt'}>
                Confirm Receipt
              </Button>
            )}
            {canEditModule('smelter') && (
              <Button icon={<FileTextOutlined />} onClick={() => setMemoModal(true)}>
                Transit Memo
              </Button>
            )}
            {['RECEIPT_CONFIRMED', 'PAYMENT_RECEIVED'].includes(dispatch.status) && canEditModule('smelter') && (
              <Button icon={<FileTextOutlined />} onClick={() => setInvoiceModal(true)}
                loading={actionLoading === 'invoice'}>
                {invoice ? 'Regenerate Bill' : 'Generate Bill'}
              </Button>
            )}
            {invoice && (
              <Popover
                trigger="click"
                open={invoiceCopyOpen}
                onOpenChange={setInvoiceCopyOpen}
                title="Which copy?"
                content={(
                  <Space>
                    <Button type="primary" onClick={() => handleDownloadInvoice('original')}>
                      Original
                    </Button>
                    <Button onClick={() => handleDownloadInvoice('duplicate')}>
                      Duplicate
                    </Button>
                  </Space>
                )}
              >
                <Button icon={<DownloadOutlined />} loading={actionLoading === 'download-invoice'}>
                  Download Invoice
                </Button>
              </Popover>
            )}
            {dispatch.status === 'PAYMENT_RECEIVED' && settlement.outstanding > 0.01
              && invoice && canEditModule('smelter') && (
              <Tooltip title="This dispatch is settled; the smelter still owes the GST inside the bill. Record it when it comes in.">
                <Button icon={<DollarOutlined />} onClick={() => setPaymentModal(true)}
                  loading={actionLoading === 'payment'}>
                  Record GST Reimbursement ({fmt(settlement.outstanding)})
                </Button>
              </Tooltip>
            )}
            {dispatch.status === 'RECEIPT_CONFIRMED' && canEditModule('smelter') && (
              <>
                <Button onClick={() => { overrideForm.setFieldsValue({ totalAmountFromSmelter: dispatch.totalAmountFromSmelter }); setOverrideModal(true); }}
                  loading={actionLoading === 'override'}>
                  Override Amount
                </Button>
                <Tooltip title={invoice
                  ? (settlement.settled > 0 ? `${fmt(settlement.toSettle)} still to be received to settle this dispatch` : '')
                  : 'Generate the bill first — the smelter pays the metal value; GST is recovered separately'}>
                  <Button type="primary" icon={<DollarOutlined />} onClick={() => setPaymentModal(true)}
                    disabled={!invoice} loading={actionLoading === 'payment'}>
                    {settlement.settled > 0 ? 'Record Part Payment' : 'Record Payment'}
                  </Button>
                </Tooltip>
              </>
            )}
            {can('smelter', 'delete') && dispatch.status !== 'PAYMENT_RECEIVED' && (
              <Button danger loading={deleting} onClick={handleDelete}>
                Delete Dispatch
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Progress */}
      <Card style={{ marginBottom: 16 }}>
        <Steps
          current={stepIdx} size="small"
          items={STATUSES.map((s) => ({ title: s.replace(/_/g, ' ') }))}
        />
      </Card>

      <Row gutter={[16, 16]}>
        {/* Dispatch info */}
        <Col xs={24} md={12}>
          <Card title="Dispatch Details" size="small">
            <Descriptions column={1} size="small">
              {canViewSmelterName && (
                <Descriptions.Item label="Smelter">{dispatch.smelterName || dispatch.smelterId?.name}</Descriptions.Item>
              )}
              {canViewContact && (
                <Descriptions.Item label="Contact">{dispatch.smelterId?.contactPerson}</Descriptions.Item>
              )}
              {canViewPhone && (
                <Descriptions.Item label="Phone">{dispatch.smelterId?.phone}</Descriptions.Item>
              )}
              <Descriptions.Item label="Batch Type">{dispatch.batchType || 'SINGLE'}</Descriptions.Item>
              <Descriptions.Item label="Dispatch Date">{new Date(dispatch.dispatchDate).toLocaleDateString('en-IN')}</Descriptions.Item>
              <Descriptions.Item label="Dispatched Net Wt">{dispatch.dispatchedNetWeight?.toFixed(3)}g</Descriptions.Item>
              <Descriptions.Item label="Dispatched Gross Wt">{dispatch.dispatchedGrossWeight?.toFixed(3)}g</Descriptions.Item>
              {dispatch.meltingLoss != null && dispatch.meltingLoss > 0 && (
                <Descriptions.Item label="Melting Loss">{dispatch.meltingLoss.toFixed(3)}g</Descriptions.Item>
              )}
              {dispatch.receiptPurityPercent != null && (
                <Descriptions.Item label="Smelter Purity">{dispatch.receiptPurityPercent.toFixed(2)}%</Descriptions.Item>
              )}
              {dispatch.refinedWeight != null && (
                <Descriptions.Item label="Refined Weight (Smelter)">{dispatch.refinedWeight.toFixed(4)}g</Descriptions.Item>
              )}
              {dispatch.rateOfferedBySmelter != null && (
                <Descriptions.Item label="Rate (₹/g on pure, excl. GST)">
                  ₹{fmt2(dispatch.rateOfferedBySmelter)}
                </Descriptions.Item>
              )}
              {dispatch.taxableAmountFromSmelter > 0 && (
                <Descriptions.Item label="Taxable Value (metal)">₹{fmt2(dispatch.taxableAmountFromSmelter)}</Descriptions.Item>
              )}
              {dispatch.gstAmountFromSmelter > 0 && (
                <Descriptions.Item label="GST in Total">₹{fmt2(dispatch.gstAmountFromSmelter)}</Descriptions.Item>
              )}
              {dispatch.totalAmountFromSmelter != null && (
                <Descriptions.Item label="Total From Smelter (incl. GST)">
                  <Text strong>{fmt(dispatch.totalAmountFromSmelter)}</Text>
                </Descriptions.Item>
              )}
              {dispatch.debtorAccountId?.code && (
                <Descriptions.Item label="Billed To (Debtors)">
                  {dispatch.debtorAccountId.code} — {dispatch.debtorAccountId.name}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        {/* Payment receipts — the instalment trail against the bill */}
        {settlement.receipts.length > 0 && (
          <Col xs={24}>
            <Card
              size="small"
              title={<span>Payment Receipts <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                ({settlement.receipts.length} instalment{settlement.receipts.length > 1 ? 's' : ''})
              </Text></span>}
              extra={canUndoReceipt && (
                <Popconfirm
                  title={`Undo receipt ${lastReceipt.seq}?`}
                  description={
                    <div style={{ maxWidth: 320, fontSize: 12 }}>
                      Its journal entry is reversed and {fmt(lastReceipt.cashAmount)} is taken back out of the
                      fund it was credited to. The outstanding goes back to{' '}
                      {fmt(round2(settlement.outstanding + lastReceipt.settledAmount))}.
                    </div>
                  }
                  okText="Yes, undo it"
                  cancelText="Cancel"
                  onConfirm={handleReverseReceipt}
                >
                  <Button size="small" danger loading={actionLoading === 'reverse-receipt'}>
                    Undo last receipt
                  </Button>
                </Popconfirm>
              )}
            >
              <Row gutter={[12, 8]} style={{ marginBottom: 10 }}>
                <Col xs={12} md={5}><Statistic title="Billed (incl. GST)" value={fmt(settlement.invoiceTotal)} valueStyle={{ fontSize: 16 }} /></Col>
                <Col xs={12} md={5}><Statistic title="Cash received" value={fmt(settlement.cashReceived)} valueStyle={{ fontSize: 16, color: '#3f8600' }} /></Col>
                <Col xs={12} md={4}><Statistic title="TDS withheld" value={fmt(settlement.tdsReceived)} valueStyle={{ fontSize: 16 }} /></Col>
                <Col xs={12} md={5}>
                  <Statistic title="Settled" value={fmt(settlement.settled)} valueStyle={{ fontSize: 16 }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>cash + TDS</Text>
                </Col>
                <Col xs={12} md={5}>
                  <Statistic
                    title={settlement.isSettled ? 'GST still to recover' : 'Still to settle'}
                    value={fmt(settlement.isSettled ? settlement.outstanding : settlement.toSettle)}
                    valueStyle={{ fontSize: 16, color: settlement.isSettled ? '#8c8c8c' : '#cf1322' }}
                  />
                </Col>
              </Row>
              <Progress
                percent={settlement.invoiceTotal > 0
                  ? Math.min(100, Math.round((settlement.settled / settlement.invoiceTotal) * 100))
                  : 0}
                success={{ percent: settlement.invoiceTotal > 0
                  ? Math.min(100, Math.round((Math.min(settlement.settled, settlement.metalValue) / settlement.invoiceTotal) * 100))
                  : 0 }}
                size="small"
                style={{ marginBottom: 10 }}
              />
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
                Green marks the metal value ({fmt(settlement.metalValue)}) — the dispatch settles once that is
                covered; anything past it is the smelter reimbursing GST.
              </Text>
              <Table
                size="small"
                rowKey={(r) => r._id || r.seq}
                dataSource={settlement.receipts}
                pagination={false}
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: '#', dataIndex: 'seq', key: 'seq', width: 44 },
                  {
                    title: 'Date', dataIndex: 'date', key: 'date', width: 110,
                    render: (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—'),
                  },
                  {
                    title: 'Received into', key: 'into', width: 260,
                    render: (_, r) => (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(r.splits || []).map((sp, i) => (
                          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <Tag color={sp.mode === 'CASH' || sp.mode === 'Cash' ? 'green' : 'blue'} style={{ margin: 0 }}>{sp.mode}</Tag>
                            <Text style={{ fontSize: 12 }}>{fmt(sp.amount)}</Text>
                            {sp.accountId?.code && (
                              <Text type="secondary" style={{ fontSize: 11 }}>{sp.accountId.code} {sp.accountId.name}</Text>
                            )}
                            {sp.reference && <Text type="secondary" style={{ fontSize: 11 }}>({sp.reference})</Text>}
                          </div>
                        ))}
                        {r.legacy && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            recorded before instalment tracking
                          </Text>
                        )}
                        {r.source === 'MANUAL_JOURNAL' && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            via manual journal {r.manualJournalReference || ''}
                          </Text>
                        )}
                        {r.fundId?.name && (
                          <Text type="secondary" style={{ fontSize: 11 }}>fund: {r.fundId.name}</Text>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: 'Cash', dataIndex: 'cashAmount', key: 'cash', width: 110, align: 'right',
                    render: (v) => fmt(v),
                  },
                  {
                    title: 'TDS', key: 'tds', width: 130, align: 'right',
                    render: (_, r) => (r.tdsAmount > 0
                      ? (
                        <Tooltip title={r.tdsAccountId?.code ? `${r.tdsAccountId.code} — ${r.tdsAccountId.name}` : ''}>
                          <span>{fmt(r.tdsAmount)}</span>
                        </Tooltip>
                      )
                      : <Text type="secondary">—</Text>),
                  },
                  {
                    title: 'Settled', dataIndex: 'settledAmount', key: 'settled', width: 120, align: 'right',
                    render: (v) => <Text strong>{fmt(v)}</Text>,
                  },
                  {
                    title: 'GST part', dataIndex: 'gstRecovered', key: 'gst', width: 110, align: 'right',
                    render: (v) => (v > 0 ? fmt(v) : <Text type="secondary">—</Text>),
                  },
                  {
                    title: 'Entry', key: 'je', width: 130,
                    render: (_, r) => (r.journalEntryId?.entryId
                      ? <Text type="secondary" style={{ fontSize: 11 }}>{r.journalEntryId.entryId}</Text>
                      : <Text type="secondary">—</Text>),
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}><Text strong>Total</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right"><Text strong>{fmt(settlement.cashReceived)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right"><Text strong>{fmt(settlement.tdsReceived)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><Text strong>{fmt(settlement.settled)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right"><Text strong>{fmt(settlement.gstRecovered)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} />
                  </Table.Summary.Row>
                )}
              />
              {!settlement.isSettled && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 10 }}
                  message={`${fmt(settlement.toSettle)} still to be received before this dispatch settles`}
                  description="Record the next instalment here, or clear it from Accounting → Manual Journal Entry by
                    picking this dispatch on the line that credits its debtors account."
                />
              )}
              {lastReceipt?.source === 'MANUAL_JOURNAL' && !settlement.isSettled && (
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                  The last receipt came from an approved manual journal entry, which owns its own ledger and fund
                  movements — correct it with an opposite manual entry rather than undoing it here.
                </Text>
              )}
            </Card>
          </Col>
        )}

        {/* Profit breakdown */}
        {dispatch.status === 'PAYMENT_RECEIVED' && canViewProfit && (
          <Col xs={24} md={12}>
            <Card title="Profit Breakdown" size="small">
              <Row gutter={[12, 12]}>
                <Col xs={12}><Statistic title="Total Purchase Cost" value={fmt(profit.totalPurchaseCost)} /></Col>
                <Col xs={12}>
                  <Statistic title="Amount from Smelter (metal)" value={fmt(profit.totalAmountFromSmelter)} />
                  {profit.gstAmountFromSmelter > 0 && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      + GST {fmt(profit.gstAmountFromSmelter)} recovered separately
                    </Text>
                  )}
                </Col>
                <Col xs={12}>
                  <Statistic
                    title="Gross Profit"
                    value={fmt(profit.grossProfit)}
                    valueStyle={{ color: (profit.grossProfit || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>on taxable value (excl. GST)</Text>
                </Col>
                <Col xs={12}>
                  <Statistic
                    title="Profit %"
                    value={`${profit.profitPercentage?.toFixed(2)}%`}
                    valueStyle={{ color: (profit.profitPercentage || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                  />
                </Col>
              </Row>
              {dispatch.paymentDate && (
                <Descriptions column={1} size="small" style={{ marginTop: 12 }}>
                  <Descriptions.Item label="Payment Date">{new Date(dispatch.paymentDate).toLocaleDateString('en-IN')}</Descriptions.Item>
                  {dispatch.paymentSplits?.length > 0 ? (
                    <Descriptions.Item label="Payment Splits">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {dispatch.paymentSplits.map((s, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Tag color={s.mode === 'CASH' || s.mode === 'Cash' ? 'green' : 'blue'} style={{ margin: 0 }}>{s.mode}</Tag>
                            <Text strong>{fmt(s.amount)}</Text>
                            {s.accountId?.code && (
                              <Text type="secondary" style={{ fontSize: 11 }}>→ {s.accountId.code} {s.accountId.name}</Text>
                            )}
                            {s.reference && <Text type="secondary" style={{ fontSize: 11 }}>({s.reference})</Text>}
                          </div>
                        ))}
                      </div>
                    </Descriptions.Item>
                  ) : (
                    <>
                      <Descriptions.Item label="Payment Mode">{dispatch.paymentMode}</Descriptions.Item>
                      <Descriptions.Item label="Reference">{dispatch.paymentReference || '—'}</Descriptions.Item>
                    </>
                  )}
                  {dispatch.tdsAmount > 0 && (
                    <Descriptions.Item label="TDS Deducted by Smelter">
                      <Text strong>{fmt(dispatch.tdsAmount)}</Text>
                      {dispatch.tdsAccountId?.code && (
                        <Text type="secondary" style={{ fontSize: 11 }}> → {dispatch.tdsAccountId.code} {dispatch.tdsAccountId.name}</Text>
                      )}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              )}
            </Card>
          </Col>
        )}

        {/* Linked batch + cases */}
        <Col xs={24}>
          <Card
            title={`Inventory Batch: ${batch.batchId || '—'} (${batch.caseCount || 0} cases)`}
            size="small"
            extra={
              batch._id && (
                <Button type="link" size="small" onClick={() => navigate(`/gold-inventory/${batch._id}`)}>
                  View Batch
                </Button>
              )
            }
          >
            <Table
              dataSource={batch.caseRefs || []}
              rowKey={(r) => r.caseId?._id || r.caseNumber}
              columns={caseColumns}
              pagination={false}
              size="small"
              scroll={{ x: 500 }}
            />
            {dispatch.items?.length ? (
              <Card size="small" style={{ marginTop: 16 }} title="Itemized Breakdown — Dispatched vs Smelter Confirmed">
                <Table
              dataSource={dispatch.items}
              rowKey={(r, idx) => idx}
              pagination={false}
              size="small"
              bordered
              scroll={{ x: 1200 }}
              columns={dispatchItemColumns}
              summary={() => {
                const total = dispatch.items.reduce((s, i) => s + (i.receiptAmount || 0), 0);
                const totalOurGross = dispatch.items.reduce((s, i) => s + (i.grossWeight || 0), 0);
                const totalReceiptGross = dispatch.items.reduce((s, i) => s + (i.receiptGrossWeight || 0), 0);
                const totalMeltingLoss = dispatch.items.reduce((s, i) => s + (i.meltingLoss || 0), 0);
                const totalReceiptNet = dispatch.items.reduce((s, i) => s + (i.receiptNetWeight || 0), 0);
                const totalDiffGross = dispatch.items.reduce((s, i) => s + (i.grossWeightDiff || 0), 0);
                const totalDiffNet = dispatch.items.reduce((s, i) => s + (i.netWeightDiff || 0), 0);
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><strong>Totals</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>{totalOurGross.toFixed(3)}g</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                    <Table.Summary.Cell index={3} />
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5}>{totalMeltingLoss.toFixed(3)}g</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{totalReceiptGross.toFixed(3)}g</Table.Summary.Cell>
                    <Table.Summary.Cell index={7} />
                    <Table.Summary.Cell index={8}>{totalReceiptNet.toFixed(4)}g</Table.Summary.Cell>
                    <Table.Summary.Cell index={9} />
                    <Table.Summary.Cell index={10}>
                      <Text strong>{fmt(total)}</Text>
                      {dispatch.totalAmountFromSmelter > 0 && (
                        <div style={{ fontSize: 10, color: '#888' }}>
                          incl. GST {fmt(dispatch.totalAmountFromSmelter)}
                        </div>
                      )}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11}>{diffCell(totalDiffGross, 3)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={12} />
                    <Table.Summary.Cell index={13}>{diffCell(totalDiffNet, 4)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
              </Card>
            ) : null}
          </Card>
        </Col>
      </Row>

      {/* Confirm Receipt Modal */}
      <Modal
        title="Confirm Smelter Receipt"
        open={receiptModal}
        onCancel={() => setReceiptModal(false)}
        onOk={() => receiptForm.submit()}
        confirmLoading={actionLoading === 'receipt'}
        okText="Confirm"
        width={screens.md ? 760 : '96vw'}
      >
        <Form form={receiptForm} layout="vertical" onFinish={handleConfirmReceipt}>
          {(() => {
            const metalWord = dispatch.metalType === 'SILVER' ? 'Silver' : 'Gold';
            const dispatchedGross = round2Wt(dispatch.dispatchedGrossWeight);
            return (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Enter the two figures from the smelter's bill"
                  description={`The pure ${metalWord.toLowerCase()} weight they settled on, and the total they are paying you including GST. Melting loss, purity, taxable value, GST and the ₹/g rate are all worked out below.`}
                />

                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={`Pure ${metalWord} Net Wt (g)`}
                      name="refinedWeight"
                      extra={`We dispatched ${dispatchedGross.toFixed(3)}g gross`}
                      rules={[
                        { required: true, message: 'Required' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (value == null || value === '') return Promise.resolve();
                            if (Number(value) <= 0) return Promise.reject(new Error('Must be greater than 0'));
                            const g = grossOverride
                              ? (Number(getFieldValue('receiptGrossWeight')) || 0)
                              : dispatchedGross;
                            if (g > 0 && Number(value) > g) {
                              return Promise.reject(new Error(`Cannot exceed the gross weight (${g.toFixed(3)}g)`));
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <InputNumber min={0} precision={3} style={{ width: '100%' }} placeholder="e.g. 134.750" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Total Amount Received (₹, incl. GST)"
                      name="totalAmountFromSmelter"
                      extra="Exactly what the smelter's bill totals to"
                      rules={[
                        { required: true, message: 'Required' },
                        { type: 'number', min: 0.01, message: 'Must be greater than 0' },
                      ]}
                    >
                      <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="e.g. 2193058" />
                    </Form.Item>
                  </Col>
                </Row>

                <Checkbox
                  checked={grossOverride}
                  style={{ marginBottom: 12 }}
                  onChange={(e) => {
                    setGrossOverride(e.target.checked);
                    if (!e.target.checked) {
                      receiptForm.setFieldsValue({ receiptGrossWeight: dispatch.dispatchedGrossWeight });
                    }
                    receiptForm.validateFields(['refinedWeight']).catch(() => {});
                  }}
                >
                  The smelter weighed a different gross
                </Checkbox>
                {grossOverride && (
                  <Form.Item label="Smelter Gross (g)" name="receiptGrossWeight" rules={[{ required: true, message: 'Required' }]}>
                    <InputNumber min={0} precision={3} style={{ width: '100%' }}
                      onChange={() => receiptForm.validateFields(['refinedWeight']).catch(() => {})} />
                  </Form.Item>
                )}

                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const gross = grossOverride
                      ? round2Wt(Number(getFieldValue('receiptGrossWeight')) || 0)
                      : dispatchedGross;
                    const net = round2Wt(Number(getFieldValue('refinedWeight')) || 0);
                    const { total, taxable, gst, cgst, sgst } = backOutGst(Number(getFieldValue('totalAmountFromSmelter')) || 0, gstRate);
                    const over = net > 0 && gross > 0 && net > gross;
                    const loss = round2Wt(Math.max(0, gross - net));
                    const purity = gross > 0 && net > 0 ? (net / gross) * 100 : 0;
                    const lossPct = gross > 0 ? (loss / gross) * 100 : 0;
                    const ourNet = round2Wt(dispatch.dispatchedNetWeight);
                    const netDiff = net > 0 ? round2Wt(net - ourNet) : 0;
                    const ratePureEx = net > 0 ? round2(taxable / net) : 0;
                    const ratePureInc = net > 0 ? round2(total / net) : 0;
                    const rateGrossEx = gross > 0 ? round2(taxable / gross) : 0;
                    const cost = Number(batch.totalPurchaseCost) || 0;
                    const profitEst = cost > 0 && taxable > 0 ? round2(taxable - cost) : null;

                    const row = (label, value, opts = {}) => (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
                        <Text strong={!!opts.strong} style={{ fontSize: 12, whiteSpace: 'nowrap', color: opts.color }}>
                          {value}
                        </Text>
                      </div>
                    );
                    const panel = (title, children, bg) => (
                      <div style={{ background: bg, border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 14px', height: '100%' }}>
                        <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</Text>
                        <Divider style={{ margin: '6px 0' }} />
                        {children}
                      </div>
                    );

                    return (
                      <>
                        {over && (
                          <Alert type="error" showIcon style={{ marginBottom: 12 }}
                            message={`Pure weight (${net.toFixed(3)}g) cannot exceed the gross weight (${gross.toFixed(3)}g).`} />
                        )}
                        <Row gutter={12}>
                          <Col xs={24} md={12} style={{ marginBottom: 12 }}>
                            {panel('Weights', (
                              <>
                                {row(grossOverride ? 'Smelter gross' : 'Dispatched gross', `${gross.toFixed(3)} g`)}
                                {row(`Pure ${metalWord.toLowerCase()} (net)`, `${net.toFixed(3)} g`, { strong: true })}
                                {row('Melting loss (gross − pure)', `${loss.toFixed(3)} g${lossPct > 0 ? ` (${lossPct.toFixed(2)}%)` : ''}`)}
                                {row('Purity (pure ÷ gross)', purity > 0 ? `${purity.toFixed(2)}%` : '—')}
                                {row('vs our assayed net', net > 0
                                  ? `${ourNet.toFixed(3)} g (${netDiff >= 0 ? '+' : ''}${netDiff.toFixed(3)})`
                                  : `${ourNet.toFixed(3)} g`,
                                { color: net > 0 && netDiff < 0 ? '#cf1322' : undefined })}
                              </>
                            ), '#fafafa')}
                          </Col>
                          <Col xs={24} md={12} style={{ marginBottom: 12 }}>
                            {panel('What the bill will say', (
                              <>
                                {row('Taxable value (metal)', `₹${fmt2(taxable)}`)}
                                {row(`CGST ${(gstRate / 2).toFixed(2)}%`, `₹${fmt2(cgst)}`)}
                                {row(`SGST ${(gstRate / 2).toFixed(2)}%`, `₹${fmt2(sgst)}`)}
                                {row(`GST @${gstRate}% total`, `₹${fmt2(gst)}`)}
                                <Divider style={{ margin: '6px 0' }} />
                                {row('Total received (incl. GST)', `₹${fmt2(total)}`, { strong: true, color: '#8C1D1D' })}
                              </>
                            ), '#fffbe6')}
                          </Col>
                        </Row>

                        <div style={{ background: '#f6ffed', border: '1px solid #d9f7be', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
                          <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Derived rates</Text>
                          <Divider style={{ margin: '6px 0' }} />
                          <Row gutter={12}>
                            <Col xs={24} md={12}>
                              {row('₹/g on pure wt — excl. GST', ratePureEx > 0 ? `₹${fmt2(ratePureEx)}` : '—', { strong: true })}
                              {row('₹/g on pure wt — incl. GST', ratePureInc > 0 ? `₹${fmt2(ratePureInc)}` : '—')}
                            </Col>
                            <Col xs={24} md={12}>
                              {row('₹/g on gross wt — excl. GST', rateGrossEx > 0 ? `₹${fmt2(rateGrossEx)}` : '—')}
                              {profitEst != null && canViewProfit && row('Est. gross profit (taxable − cost)', `₹${fmt2(profitEst)}`,
                                { strong: true, color: profitEst >= 0 ? '#3f8600' : '#cf1322' })}
                            </Col>
                          </Row>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            The ₹/g rate is no longer typed in — it is the taxable value divided by the pure weight,
                            which is exactly the rate printed on the tax invoice.
                          </Text>
                        </div>

                        {total > 0 && (
                          <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
                            {rupeesInWords(total)}
                          </div>
                        )}

                        {dispatch.items?.length > 0 && net > 0 && total > 0 && (() => {
                          // One smelter bill covering many items: the pure weight and the
                          // taxable value are split across the rows pro-rata, exactly as the
                          // backend stores them.
                          const its = dispatch.items;
                          const grossParts = allocate(gross, its.map((i) => Number(i.grossWeight) || 0), 4);
                          const netParts = allocate(net, its.map((i) => Number(i.netWeight) || Number(i.grossWeight) || 0), 4);
                          const amtParts = allocate(taxable, netParts, 2);
                          return (
                            <div style={{ marginBottom: 12 }}>
                              <Text strong style={{ fontSize: 12 }}>Split across the {its.length} items</Text>
                              <div style={{ overflowX: 'auto', marginTop: 6 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: '#fafafa' }}>
                                      <th style={thStyle}>Item</th>
                                      <th style={thStyle}>Our Gross (g)</th>
                                      <th style={thStyle}>Our Net (g)</th>
                                      <th style={thStyle}>Pure Share (g)</th>
                                      <th style={thStyle}>Purity %</th>
                                      <th style={thStyle}>Taxable (₹)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {its.map((it, i) => (
                                      <tr key={it.label || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ ...tdStyle, textAlign: 'left' }}>{it.label || `Item ${i + 1}`}</td>
                                        <td style={tdStyle}>{(Number(it.grossWeight) || 0).toFixed(3)}</td>
                                        <td style={tdStyle}>{(Number(it.netWeight) || 0).toFixed(4)}</td>
                                        <td style={tdStyle}>{netParts[i].toFixed(4)}</td>
                                        <td style={tdStyle}>{grossParts[i] > 0 ? `${((netParts[i] / grossParts[i]) * 100).toFixed(2)}%` : '—'}</td>
                                        <td style={tdStyle}>{fmt2(amtParts[i])}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr style={{ borderTop: '2px solid #bbb', background: '#f0f0f0', fontWeight: 700 }}>
                                      <td style={{ ...tdStyle, textAlign: 'left' }}>Totals</td>
                                      <td style={tdStyle}>{gross.toFixed(3)}</td>
                                      <td style={tdStyle}>{ourNet.toFixed(4)}</td>
                                      <td style={tdStyle}>{net.toFixed(4)}</td>
                                      <td style={tdStyle}>{purity > 0 ? `${purity.toFixed(2)}%` : '—'}</td>
                                      <td style={tdStyle}>{fmt2(taxable)}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    );
                  }}
                </Form.Item>
              </>
            );
          })()}
          <Divider style={{ margin: '16px 0 12px' }} />
          <Form.Item
            label="Sundry Debtors Account"
            name="debtorAccountId"
            rules={[{ required: true, message: 'Pick the account the smelter will be billed to' }]}
            extra="When the bill is generated this account is debited with the GST-inclusive total (against Metal In Transit, GST and profit), and the smelter's payment clears it back to zero."
          >
            <Select
              placeholder={accountsLoaded ? 'Select account' : 'Loading accounts…'}
              loading={!accountsLoaded}
              showSearch
              optionFilterProp="label"
              options={ACCOUNT_TYPE_ORDER
                .map((type) => ({
                  label: type,
                  options: accounts
                    .filter((a) => a.type === type)
                    .map((a) => ({ value: a._id, label: `${a.code} — ${a.name}` })),
                }))
                .filter((g) => g.options.length)}
            />
          </Form.Item>
          <Form.Item label="Notes" name="receiptNotes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Record Payment Modal */}
      <Modal
        title={settlement.isSettled ? 'Record GST Reimbursement' : 'Record Smelter Payment'}
        open={paymentModal}
        onCancel={() => setPaymentModal(false)}
        onOk={() => paymentForm.submit()}
        confirmLoading={actionLoading === 'payment'}
        okText="Record & Calculate Profit"
        okButtonProps={{ disabled: fundsLoaded && !smelterFlaggedFund }}
        width={760}
      >
        {fundsLoaded && !smelterFlaggedFund && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="No Smelter Payment Fund is set"
            description={'Payment recording is blocked until one fund is flagged as the Smelter Payment Fund. Go to Accounting → Manage Company Funds, edit a fund, and enable "Smelter Payment Fund".'}
          />
        )}
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handleRecordPayment}
        >
          <Form.Item
            name="fundId"
            label="Credit Into Fund"
            rules={[{ required: true, message: 'Select the destination fund' }]}
            extra="The full amount received lands in this fund (Manage Company Funds)."
          >
            <Select
              placeholder={fundsLoaded ? 'Select fund' : 'Loading funds…'}
              loading={!fundsLoaded}
              showSearch
              optionFilterProp="label"
              options={funds.map((f) => ({
                value: f._id,
                label: `${f.name} — ${fmt(f.balance)}${f.isSmelterPaymentFund ? '  ★ Smelter Fund' : ''}`,
              }))}
            />
          </Form.Item>
          {invoice && (
            <div style={{ marginBottom: 12, padding: '8px 10px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 4, fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Taxable Value</span><strong>{fmt(invoice.taxableValue)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{invoice.gstType === 'INTRA' ? `CGST + SGST @${invoice.gstRatePercent}%` : `IGST @${invoice.igstPercent}%`}</span>
                <strong>{fmt(invoice.gstAmount)}</strong>
              </div>
              {invoice.roundOff ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999' }}>
                  <span>…of which round off (inside the taxable value)</span><span>{fmt(invoice.roundOff)}</span>
                </div>
              ) : null}
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999' }}>
                <span>Invoice Total (incl. GST)</span><span>{fmt(invoice.total)}</span>
              </div>
              <Divider style={{ margin: '6px 0' }} />
              {settlement.settled > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#3f8600' }}>
                    <span>Already settled ({settlement.receipts.length} receipt{settlement.receipts.length > 1 ? 's' : ''})</span>
                    <strong>{fmt(settlement.settled)}</strong>
                  </div>
                  {settlement.tdsReceived > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999' }}>
                      <span>…of which TDS withheld</span><span>{fmt(settlement.tdsReceived)}</span>
                    </div>
                  )}
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Still to settle this dispatch (metal value)</span>
                <strong style={{ color: '#8C1D1D' }}>{fmt(settlement.toSettle)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999' }}>
                <span>Maximum receivable now (incl. GST)</span><span>{fmt(settlement.outstanding)}</span>
              </div>
              <div style={{ marginTop: 4, color: '#888' }}>
                Invoice {invoice.invoiceNo}. The GST of {fmt(invoice.gstAmount)} is recovered separately via the
                GST ledger, so the dispatch closes once the metal value is covered.
              </div>
            </div>
          )}
          <p style={{ marginBottom: 12, fontSize: 13 }}>
            {settlement.isSettled ? (
              <>This dispatch is already settled at its metal value. What is left is the GST inside the bill —
                up to <strong>{fmt(settlement.outstanding)}</strong> — which still has to clear the debtor
                when the smelter reimburses it.</>
            ) : (
              <>Enter what the smelter is paying <strong>now</strong> — a part payment is fine. The dispatch stays
                open and can take further receipts until <strong>{fmt(settlement.toSettle)}</strong> more has come
                in (cash + TDS), at which point it settles and profit is calculated. It can take at most{' '}
                <strong>{fmt(settlement.outstanding)}</strong> in total, the rest of that being GST.</>
            )}
          </p>

          <Form.List
            name="paymentSplits"
            rules={[
              {
                validator: async (_, splits) => {
                  if (!splits || splits.length === 0) throw new Error('Add at least one payment split');
                  const cash = splits.reduce((s, p) => s + (Number(p?.amount) || 0), 0);
                  const total = round2(cash + (Number(paymentForm.getFieldValue('tdsAmount')) || 0));
                  if (total <= 0) throw new Error('Enter the amount received (or the TDS deducted)');
                  if (total > settlement.outstanding + 0.01) {
                    throw new Error(`${fmt(total)} is more than the ${fmt(settlement.outstanding)} still outstanding on this bill`);
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                <Row gutter={8} style={{ marginBottom: 4, fontSize: 11, color: '#888' }}>
                  <Col flex="120px">Mode</Col>
                  <Col flex="auto">Debit Account (money received into)</Col>
                  <Col flex="140px">Amount</Col>
                  <Col flex="140px">Reference</Col>
                  <Col flex="32px" />
                </Row>
                {fields.map((field, index) => (
                  <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                    <Col flex="120px">
                      <Form.Item
                        name={[field.name, 'mode']}
                        style={{ margin: 0 }}
                        rules={[{ required: true, message: 'Select mode' }]}
                      >
                        <Select
                          size="small"
                          onChange={(mode) => {
                            // Follow the mode with the matching bank/cash account, so
                            // switching Cash ↔ Bank does not leave a stale account.
                            const match = accounts.find((a) => a.code === MODE_DEFAULT_CODE(mode));
                            if (match) {
                              const splits = [...(paymentForm.getFieldValue('paymentSplits') || [])];
                              splits[field.name] = { ...splits[field.name], accountId: match._id };
                              paymentForm.setFieldsValue({ paymentSplits: splits });
                            }
                          }}
                        >
                          {PAYMENT_MODES.map((m) => (
                            <Select.Option key={m.value} value={m.value}>{m.label}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col flex="auto" style={{ minWidth: 0 }}>
                      <Form.Item
                        name={[field.name, 'accountId']}
                        style={{ margin: 0 }}
                        rules={[{ required: true, message: 'Pick the account this money is debited to' }]}
                      >
                        <Select
                          size="small"
                          style={{ width: '100%' }}
                          placeholder={accountsLoaded ? 'Bank / Cash account' : 'Loading accounts…'}
                          loading={!accountsLoaded}
                          showSearch
                          optionFilterProp="label"
                          options={ACCOUNT_TYPE_ORDER
                            .map((type) => ({
                              label: type,
                              options: accounts
                                .filter((a) => a.type === type)
                                .map((a) => ({ value: a._id, label: `${a.code} — ${a.name}` })),
                            }))
                            .filter((g) => g.options.length)}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="140px">
                      <Form.Item
                        name={[field.name, 'amount']}
                        style={{ margin: 0 }}
                        rules={[{ required: true, message: 'Enter amount' }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          size="small"
                          placeholder="Amount (₹)"
                          prefix="₹"
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="140px">
                      <Form.Item name={[field.name, 'reference']} style={{ margin: 0 }}>
                        <Input size="small" placeholder="UTR / Ref" />
                      </Form.Item>
                    </Col>
                    <Col flex="32px">
                      {fields.length > 1 && (
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </Col>
                  </Row>
                ))}

                {/* Live total row — cash in hand plus the TDS the smelter withheld */}
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const splits = getFieldValue('paymentSplits') || [];
                    const cash = round2(splits.reduce((s, p) => s + (Number(p?.amount) || 0), 0));
                    const tds = round2(getFieldValue('tdsAmount'));
                    const total = round2(cash + tds);
                    const overCap = total > settlement.outstanding + 0.01;
                    const settlesNow = !overCap && total >= settlement.toSettle - 0.01;
                    const remaining = round2(Math.max(0, settlement.toSettle - total));
                    const ok = !overCap && total > 0;
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, padding: '6px 8px', background: ok ? '#f6ffed' : '#fff7e6', borderRadius: 4, border: `1px solid ${ok ? '#b7eb8f' : '#ffd591'}` }}>
                        <Text style={{ fontSize: 12 }}>
                          Cash received: <strong>{fmt(cash)}</strong>
                          {tds > 0 && <> &nbsp;+ TDS: <strong>{fmt(tds)}</strong> = <strong>{fmt(total)}</strong></>}
                        </Text>
                        <Text style={{ fontSize: 12, color: overCap ? '#d48806' : '#3f8600' }}>
                          {overCap
                            ? `Over the ${fmt(settlement.outstanding)} outstanding`
                            : settlement.isSettled
                              ? `${fmt(round2(settlement.outstanding - total))} GST would still be outstanding`
                              : settlesNow
                                ? 'Settles this dispatch ✓'
                                : `${fmt(remaining)} would still be due`}
                        </Text>
                      </div>
                    );
                  }}
                </Form.Item>

                <Button
                  type="dashed"
                  onClick={() => add({
                    mode: 'CASH',
                    amount: null,
                    reference: '',
                    accountId: accounts.find((a) => a.code === MODE_DEFAULT_CODE('CASH'))?._id,
                  })}
                  icon={<PlusOutlined />}
                  size="small"
                  style={{ width: '100%', marginBottom: 4 }}
                >
                  Add another payment mode
                </Button>
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>

          <Divider style={{ margin: '16px 0 8px' }} orientation="left" orientationMargin={0}>
            <span style={{ fontSize: 12 }}>TDS deducted by the smelter</span>
          </Divider>
          <Row gutter={8} align="top">
            <Col flex="180px">
              <Form.Item
                name="tdsAmount"
                style={{ marginBottom: 8 }}
                rules={[{ type: 'number', min: 0, message: 'Cannot be negative' }]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  size="small"
                  placeholder="TDS amount (₹)"
                  prefix="₹"
                  onChange={() => paymentForm.validateFields([['paymentSplits']]).catch(() => {})}
                />
              </Form.Item>
            </Col>
            <Col flex="auto" style={{ minWidth: 0 }}>
              <Form.Item
                name="tdsAccountId"
                style={{ marginBottom: 8 }}
                rules={[({ getFieldValue }) => ({
                  required: (Number(getFieldValue('tdsAmount')) || 0) > 0,
                  message: 'Pick the account the TDS is debited to',
                })]}
                dependencies={['tdsAmount']}
              >
                <Select
                  size="small"
                  placeholder={accountsLoaded ? 'TDS account (e.g. 1340 — TDS Receivable)' : 'Loading accounts…'}
                  loading={!accountsLoaded}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={ACCOUNT_TYPE_ORDER
                    .map((type) => ({
                      label: type,
                      options: accounts
                        .filter((a) => a.type === type)
                        .map((a) => ({ value: a._id, label: `${a.code} — ${a.name}` })),
                    }))
                    .filter((g) => g.options.length)}
                />
              </Form.Item>
            </Col>
          </Row>
          <p style={{ color: '#888', fontSize: 11, marginTop: 0, marginBottom: 8 }}>
            Enter the exact amount the smelter withheld — it is not a percentage of anything here.
            The TDS never reaches the bank, so it is not credited to the fund; it is debited to this
            account and clears the debtor along with the cash.
          </p>

          {/* What this payment will post to the ledger */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const splits = getFieldValue('paymentSplits') || [];
              const tds = round2(getFieldValue('tdsAmount'));
              const cash = round2(splits.reduce((s, p) => s + (Number(p?.amount) || 0), 0));
              const nameOf = (accountId, fallback) => {
                const a = accounts.find((x) => x._id === accountId);
                return a ? `${a.code} — ${a.name}` : fallback;
              };
              const leg = (label, amount, side, key) => (
                <Row justify="space-between" style={{ fontSize: 12 }} key={key || label}>
                  <Col>{side === 'Dr' ? <Text strong>Dr</Text> : <Text type="secondary">&nbsp;&nbsp;Cr</Text>} {label}</Col>
                  <Col>{fmt2(amount)}</Col>
                </Row>
              );
              return (
                <Alert
                  type="info"
                  showIcon={false}
                  style={{ marginBottom: 12 }}
                  message={<span style={{ fontSize: 12 }}>Journal entry this payment will post</span>}
                  description={
                    <div style={{ marginTop: 4 }}>
                      {splits
                        .filter((sp) => (Number(sp?.amount) || 0) > 0)
                        .map((sp, i) => leg(nameOf(sp?.accountId, 'Bank / Cash'), sp.amount, 'Dr', `sp-${i}`))}
                      {tds > 0 && leg(nameOf(getFieldValue('tdsAccountId'), 'TDS Receivable'), tds, 'Dr', 'tds')}
                      {leg(
                        dispatch.debtorAccountId?.code
                          ? `${dispatch.debtorAccountId.code} — ${dispatch.debtorAccountId.name}`
                          : 'Sundry Debtors',
                        round2(cash + tds), 'Cr', 'debtor',
                      )}
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                        {fmt2(cash)} is credited into the selected fund. What is left in the debtor after this
                        receipt — {fmt2(Math.max(0, round2(settlement.outstanding - cash - tds)))} — stays there
                        until the smelter pays it or reimburses the GST.
                      </Text>
                    </div>
                  }
                />
              );
            }}
          </Form.Item>

          <p style={{ color: '#888', fontSize: 12, margin: 0 }}>
            Profit will be automatically calculated and distributed to all linked cases.
            TDS is an advance tax asset — it does not reduce profit.
          </p>
        </Form>
      </Modal>
      {/* Override Amount Modal */}
      <Modal
        title="Override Total Amount from Smelter"
        open={overrideModal}
        onCancel={() => setOverrideModal(false)}
        onOk={() => overrideForm.submit()}
        confirmLoading={actionLoading === 'override'}
        okText="Update Amount"
      >
        <Form form={overrideForm} layout="vertical" onFinish={handleOverrideAmount}>
          <Form.Item label="Total Amount from Smelter (₹)" name="totalAmountFromSmelter" rules={[{ required: true }]}>
            <InputNumber min={0} precision={2} style={{ width: '100%' }}
              placeholder="Enter negotiated total amount" />
          </Form.Item>
        </Form>
        <p style={{ color: '#888', marginTop: 8, fontSize: 12 }}>
          GST-inclusive, same as on Confirm Receipt. Changing it re-derives the taxable value,
          the GST split and the ₹/g rate from the new amount.
        </p>
      </Modal>

      {/* Generate Bill (Tax Invoice) Modal */}
      <Modal
        title="Generate Tax Invoice"
        open={invoiceModal}
        onCancel={() => setInvoiceModal(false)}
        onOk={() => invoiceForm.submit()}
        confirmLoading={actionLoading === 'invoice'}
        okText={invoice ? 'Regenerate & Download' : 'Generate & Download'}
        width={screens.md ? 860 : '96vw'}
        style={{ top: screens.md ? 48 : 12, maxWidth: 860 }}
      >
        <Form form={invoiceForm} layout="vertical" onFinish={handleGenerateInvoice} requiredMark={false}>
          {/* What this bill posts to the ledger — recomputes as the figures are edited. */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const grand = grandTotalOf(getFieldValue);
              const isIntra = getFieldValue('gstType') !== 'INTER';
              const gst = isIntra
                ? round2((Number(getFieldValue('cgstAmount')) || 0) + (Number(getFieldValue('sgstAmount')) || 0))
                : round2(Number(getFieldValue('igstAmount')) || 0);
              const taxable = round2(Number(getFieldValue('taxableValue')) || 0);
              const residue = round2(grand - taxable - gst);
              const cost = round2(batch.totalPurchaseCost);
              const isSilver = dispatch.metalType === 'SILVER';
              const metal = isSilver ? 'Silver' : 'Gold';
              const leg = (label, amount, side) => (
                <Row justify="space-between" style={{ fontSize: 12 }} key={label}>
                  <Col>{side === 'Dr' ? <Text strong>Dr</Text> : <Text type="secondary">&nbsp;&nbsp;Cr</Text>} {label}</Col>
                  <Col>{fmt2(amount)}</Col>
                </Row>
              );
              return (
                <Alert
                  type="info"
                  showIcon={false}
                  style={{ marginBottom: 16 }}
                  message={<span style={{ fontSize: 12 }}>Journal entries this bill will post</span>}
                  description={
                    <div style={{ marginTop: 4 }}>
                      {leg(dispatch.debtorAccountId?.name || 'Sundry Debtors', grand, 'Dr')}
                      {leg(isSilver ? 'Silver Sales Revenue' : 'Gold Sales Revenue', taxable, 'Cr')}
                      {gst > 0 && leg('Taxes Payable (output GST)', gst, 'Cr')}
                      {residue !== 0 && leg('Round-off', Math.abs(residue), residue > 0 ? 'Cr' : 'Dr')}
                      {cost > 0 && (
                        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed #d9d9d9' }}>
                          {leg('Cost of Goods Sold', cost, 'Dr')}
                          {leg(`${metal} In Transit`, cost, 'Cr')}
                        </div>
                      )}
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                        Gross profit {fmt2(taxable - cost)} shows in the P&amp;L as revenue minus COGS.
                        The smelter's payment later reduces {dispatch.debtorAccountId?.name || 'Sundry Debtors'} by the cash received.
                        {invoice && ' Regenerating reverses the previous entries first.'}
                      </Text>
                    </div>
                  }
                />
              );
            }}
          </Form.Item>

          {/* Header meta */}
          <Row gutter={12}>
            <Col xs={24} md={7}>
              <Form.Item label="Invoice No" name="invoiceNo" rules={[{ required: true }]}
                tooltip={invoice ? 'Issued number — preserved on regeneration.' : 'Pre-filled with the next AGS number. Editable before saving.'}>
                <Input placeholder="AGS-2026-00130" disabled={!!invoice} />
              </Form.Item>
            </Col>
            <Col xs={24} md={5}>
              <Form.Item label="Invoice Date" name="invoiceDate" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" inputReadOnly />
              </Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item label="Metal Delivered By" name="deliveredByUserId" rules={[{ required: true, message: 'Select employee' }]}>
                <Select showSearch placeholder="Select employee" optionFilterProp="label"
                  options={employees.map((e) => ({ value: e._id, label: e.employeeCode ? `${e.name} (${e.employeeCode})` : e.name }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={5}>
              <Form.Item label="GST Type" name="gstType">
                <Select onChange={recomputeTotals}
                  options={[
                    { value: 'INTRA', label: 'CGST + SGST' },
                    { value: 'INTER', label: 'IGST' },
                  ]} />
              </Form.Item>
            </Col>
          </Row>

          {/* Buyer (Refinery) details and Place of Supply are hidden from this form —
              they are auto-filled from the selected smelter on the server. */}

          {/* Goods — single line item */}
          <Divider orientation="left" style={{ margin: '0 0 12px', fontSize: 13 }}>Goods</Divider>
          <Form.List name="lineItems">
            {(fields) => (fields.length ? (
              <Row gutter={[16, 4]} key={fields[0].key}>
                {/* Line 1 — identity + weights (Gross = Net + Melt Loss) */}
                <Col xs={24} md={9}>
                  <Form.Item label="Description of Goods" name={[fields[0].name, 'description']} rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="Old melted Gold" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label="HSN" name={[fields[0].name, 'hsn']}>
                    <Input placeholder={hsnForMetal(dispatch?.metalType)} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label="Gross Wt (g)" name={[fields[0].name, 'grossWeight']}>
                    <InputNumber min={0} precision={3} style={{ width: '100%' }} onChange={() => onLineGrossChange(fields[0].name)} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label="Melting Loss (g)" name={[fields[0].name, 'meltingLoss']} tooltip="Gross − Net. Editing this recalculates Net.">
                    <InputNumber min={0} precision={3} style={{ width: '100%' }} onChange={() => onLineMeltLossChange(fields[0].name)} />
                  </Form.Item>
                </Col>
                {/* Line 2 — net, and the derived pre-GST rate + amount */}
                <Col xs={12} md={8}>
                  <Form.Item label="Net Wt (g)" name={[fields[0].name, 'netWeight']}>
                    <InputNumber min={0} precision={3} style={{ width: '100%' }} onChange={() => onLineNetChange(fields[0].name)} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item label="Rate (₹/g, excl. GST)" name={[fields[0].name, 'rate']} tooltip="Pre-GST rate, derived from the taxable value ÷ net weight.">
                    <InputNumber min={0} precision={2} readOnly style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Amount (₹, excl. GST)" name={[fields[0].name, 'amount']} tooltip="Taxable value of the line (derived from the GST-inclusive total).">
                    <InputNumber min={0} precision={2} readOnly style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            ) : null)}
          </Form.List>

          {/* Tax summary — GST-inclusive: Total is the anchor, taxable + GST derived */}
          <Divider orientation="left" style={{ margin: '0 0 12px', fontSize: 13 }}>Tax & Total</Divider>
          <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 14px' }}>
            {/* Anchor: the GST-inclusive amount the smelter pays */}
            <Row align="middle" justify="space-between" style={{ marginBottom: 8 }} gutter={8}>
              <Col flex="auto"><Text strong>Total from Smelter (incl. GST)</Text></Col>
              <Col flex="140px">
                <Form.Item name="total" style={{ margin: 0 }} rules={[{ required: true }]}>
                  <InputNumber size="small" min={0} precision={2} style={{ width: '100%' }} onChange={recomputeTotals} />
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const isInter = getFieldValue('gstType') === 'INTER';
                const readRow = (label, field) => (
                  <Row align="middle" justify="space-between" style={{ marginBottom: 8 }} gutter={8}>
                    <Col flex="auto"><Text style={{ color: '#555' }}>{label}</Text></Col>
                    <Col flex="120px">
                      <Form.Item name={field} style={{ margin: 0 }}>
                        <InputNumber size="small" precision={2} readOnly style={{ width: '100%', border: 'none', background: 'transparent' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
                const pctReadRow = (label, pctField, amtField) => (
                  <Row align="middle" justify="space-between" style={{ marginBottom: 8 }} gutter={8}>
                    <Col flex="auto">
                      <Space size={4}>
                        <Text style={{ color: '#555' }}>{label} @</Text>
                        <Form.Item name={pctField} noStyle>
                          <InputNumber size="small" min={0} precision={2} style={{ width: 60 }} onChange={recomputeTotals} />
                        </Form.Item>
                        <Text style={{ color: '#555' }}>%</Text>
                      </Space>
                    </Col>
                    <Col flex="120px">
                      <Form.Item name={amtField} style={{ margin: 0 }}>
                        <InputNumber size="small" precision={2} readOnly style={{ width: '100%', border: 'none', background: 'transparent' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
                // Round Off is applied to the taxable value, so the row above it is the
                // taxable backed out of the anchor and the row below it is what is billed.
                const roundOff = round2(getFieldValue('roundOff'));
                const billedTaxable = round2(getFieldValue('taxableValue'));
                const baseTaxable = round2(billedTaxable - roundOff);
                const staticRow = (label, value, opts = {}) => (
                  <Row align="middle" justify="space-between" style={{ marginBottom: 8 }} gutter={8}>
                    <Col flex="auto"><Text style={{ color: opts.strong ? '#333' : '#555' }} strong={!!opts.strong}>{label}</Text></Col>
                    <Col flex="120px" style={{ textAlign: 'right', paddingRight: 11 }}>
                      <Text strong={!!opts.strong}>{fmt2(value)}</Text>
                    </Col>
                  </Row>
                );
                return (
                  <>
                    {staticRow('Taxable Value (before round off)', baseTaxable)}
                    <Row align="middle" justify="space-between" style={{ marginBottom: 8 }} gutter={8}>
                      <Col flex="auto">
                        <Text style={{ color: '#555' }}>Round Off</Text>
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                          (adjusts the taxable value — GST and the total follow)
                        </Text>
                      </Col>
                      <Col flex="120px">
                        <Form.Item name="roundOff" style={{ margin: 0 }}>
                          <InputNumber size="small" precision={2} style={{ width: '100%' }} onChange={recomputeTotals} />
                        </Form.Item>
                      </Col>
                    </Row>
                    {readRow('Taxable Value (billed)', 'taxableValue')}
                    {isInter ? pctReadRow('IGST', 'igstPercent', 'igstAmount') : (
                      <>
                        {pctReadRow('CGST', 'cgstPercent', 'cgstAmount')}
                        {pctReadRow('SGST', 'sgstPercent', 'sgstAmount')}
                      </>
                    )}
                  </>
                );
              }}
            </Form.Item>

            <Divider style={{ margin: '4px 0 10px' }} />
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const total = grandTotalOf(getFieldValue);
                const anchor = round2(getFieldValue('total'));
                const moved = round2(total - anchor);
                return (
                  <>
                    <Row align="middle" justify="space-between">
                      <Col><Text strong style={{ fontSize: 15, color: '#8C1D1D' }}>TOTAL</Text></Col>
                      <Col><Text strong style={{ fontSize: 15, color: '#8C1D1D' }}>₹ {fmt2(total)}</Text></Col>
                    </Row>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8C1D1D' }}>{rupeesInWords(total)}</div>
                    <div style={{ marginTop: 2, fontSize: 11, color: '#999' }}>The smelter pays this GST-inclusive total; GST is recoverable via the GST ledger.</div>
                    {moved !== 0 && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#d46b08' }}>
                        The round off moved the billed total {moved > 0 ? 'up' : 'down'} by ₹{fmt2(Math.abs(moved))} from
                        the ₹{fmt2(anchor)} recorded on Confirm Receipt. Generating the bill updates the dispatch to this figure.
                      </div>
                    )}
                  </>
                );
              }}
            </Form.Item>
          </div>

          <Form.Item label="Notes" name="notes" style={{ marginTop: 12, marginBottom: 0 }}>
            <Input.TextArea rows={2} placeholder="Optional notes for this invoice" />
          </Form.Item>
        </Form>
      </Modal>

      <TransitMemoModal
        open={memoModal}
        onClose={() => setMemoModal(false)}
        prefill={{
          smelterId: dispatch.smelterId?._id || dispatch.smelterId,
          metalType: dispatch.metalType || 'GOLD',
          dispatchId: dispatch._id,
          dispatchNumber: dispatch.dispatchId,
          inventoryBatchId: dispatch.inventoryBatchId?._id || dispatch.inventoryBatchId,
          batchNumber: dispatch.inventoryBatchId?.batchId || '',
          items: (dispatch.items || []).length
            ? dispatch.items.map((it) => ({
              description: it.label || `Melted ${dispatch.metalType === 'SILVER' ? 'Silver' : 'Gold'} Jwellery`,
              itemCount: 1,
              grossWeight: it.grossWeight || 0,
              remarks: 'Company Stock',
            }))
            : [{
              description: `Melted ${dispatch.metalType === 'SILVER' ? 'Silver' : 'Gold'} Jwellery`,
              itemCount: 1,
              grossWeight: dispatch.dispatchedGrossWeight || 0,
              remarks: 'Company Stock',
            }],
          totalWeight: dispatch.dispatchedGrossWeight || 0,
        }}
      />
    </div>
  );
}
